import { orderBy } from 'lodash';
import {
  IManagerApiModuleCreatorPlatformInsight,
  IManagerApiModuleManagerBenchmark,
  IManagerApiModuleManagerPlatformInsight,
} from '~/clientApi/managers.model';
import {
  MONTH_NAME_DAY_YEAR_FORMAT,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORMS_NAMES,
} from '~/constants';
import { getCreatorLocation, getMainPlatformData } from '~/core/utils';
import {
  ICreator,
  ICreatorTtInsight,
  ICreatorYtInsight,
  OptionType,
} from '~/types';
import { formatAbbr, formatDate, formatPercentage } from '~/utils';

import {
  getBasicEmailFromCreator,
  getPlatformsDataForEmailTemp,
} from './emailTemplate';

const getFilteredGenders = (genders: OptionType<number>[] | undefined) =>
  genders
    ? genders.filter(({ label }) => label.match(/male|female/gi))
    : undefined;

const hasBenchmarkValues = (array: (number | undefined)[]) =>
  array.some((benchmark: number | undefined) => benchmark && benchmark > 0);

const hasInsightValues = (insights: OptionType<number>[] | undefined) =>
  insights?.some((i) => i.value > 0);

const formatPercentageWithOneDecimal = (
  value?: number,
  numberOfDecimals: number = 1,
) => formatPercentage(value, 'n/a', numberOfDecimals);
export const getPlatformInsightsForDetailedEmail = ({
  countries,
  genders,
  ...insight
}:
  | IManagerApiModuleCreatorPlatformInsight
  | ICreatorTtInsight
  | ICreatorYtInsight) => ({
  ...insight,
  countries: countries
    ? orderBy(countries, ['value'], ['desc']).slice(0, 3)
    : undefined,
  ages: insight.agesShort || insight.ages,
  genders: getFilteredGenders(genders),
  showCountries: hasInsightValues(countries),
  showGenders: hasInsightValues(getFilteredGenders(genders)),
  showAges:
    hasInsightValues(insight.agesShort) || hasInsightValues(insight.ages),
});

export const getDetailedTemplateData = (
  creator: ICreator,
  benchmarks: IManagerApiModuleManagerBenchmark[] = [],
  insights: IManagerApiModuleManagerPlatformInsight[] = [],
) => {
  const { age } = creator;
  const location = getCreatorLocation(creator);
  const { avatar, name } = getMainPlatformData(creator);
  const creatorBenchmarks = benchmarks.find(({ id }) => id === creator.id);
  const creatorInsights = insights.find(({ id }) => id === creator.id);
  const platforms = getPlatformsDataForEmailTemp(
    creator.platforms,
    creator.isOffPlatform,
  );

  return {
    age,
    avatar,
    benchmarks: creatorBenchmarks?.benchmarks,
    insight:
      creatorInsights?.insights.instagram &&
      getPlatformInsightsForDetailedEmail(creatorInsights.insights.instagram),
    location,
    name,
    platforms,
    ttInsight:
      creatorInsights?.insights.tiktok &&
      getPlatformInsightsForDetailedEmail(creatorInsights.insights.tiktok),
    ytInsight:
      creatorInsights?.insights.youtube &&
      getPlatformInsightsForDetailedEmail(creatorInsights.insights.youtube),
  };
};

export const getDetailEmailFromCreator = (
  creator: ICreator,
  alignmentFFEnabled: boolean,
  talentLocationFFEnabled: boolean,
  benchmarks?: IManagerApiModuleManagerBenchmark[],
  insights?: IManagerApiModuleManagerPlatformInsight[],
) => {
  const data = getDetailedTemplateData(creator, benchmarks, insights);
  const basicTemplate = getBasicEmailFromCreator(
    creator,
    alignmentFFEnabled,
    talentLocationFFEnabled,
  );
  return `
    ${basicTemplate ? basicTemplate : ''}
    <div>
      ${
        data.platforms.instagram?.isConnected && data.insight
          ? getInstagramDetailEmail(data, creator, alignmentFFEnabled)
          : ''
      }
      ${
        data.platforms.tiktok?.isConnected && data.ttInsight
          ? getTiktokDetailEmail(data, creator, alignmentFFEnabled)
          : ''
      }
      ${
        data.platforms.youtube?.isConnected && data.ytInsight
          ? getYoutubeDetailEmail(data, creator, alignmentFFEnabled)
          : ''
      }
    </div>
  `;
};

// ${data.platforms.youtube?.isConnected && getYoutubeDetailEmail(data, creator)}
const getMultiplierString = (multiplier: number): string =>
  isNaN(multiplier) || multiplier < 2 ? '' : `${multiplier}x `;
const renderCreatorPlatformEngagementDetailLevelInfo = (
  creator: ICreator,
  platformName: SOCIAL_PLATFORMS,
) => {
  if (!creator) return '';
  const handlerName =
    platformName === SOCIAL_PLATFORMS.youtube
      ? creator?.platforms?.[platformName]?.name
      : creator?.platforms?.[platformName]?.handle;
  return `<p style="font-family: 'Arial', sans-serif; margin-bottom: 16px; padding: 8px; line-height: 14px; color: #9C8B81; font-size: 11px; font-weight: 400; border-radius: 8px; background-color: #F9F7F6;"><img style="vertical-align: middle; width: 16px; height: 16px; margin-right: 8px;" src="https://assets.usefoam.com/extension/connected-platform.png" alt="" /><span style="font-weight: 700;">${
    platformName !== 'youtube' ? '@' : ''
  }${handlerName}'s ${
    SOCIAL_PLATFORMS_NAMES[platformName]
  } engagement is ${getMultiplierString(
    creator?.platforms?.[platformName]?.higher ?? 0,
  )}higher</span> than creators with a similar following</p>`;
};

const getInstagramDetailEmail = (
  data: ReturnType<typeof getDetailedTemplateData>,
  creator: ICreator,
  alignmentFFEnabled: boolean,
) => {
  const { benchmarks, insight, platforms } = data;

  const hasPostsHighlightsValues = hasBenchmarkValues([
    benchmarks?.igAvgFeedReelsFollowerEngRate,
    benchmarks?.igAvgFeedReelsReachEngRate,
    benchmarks?.igAvgFeedReelsEngagements,
  ]);
  const hasStoriesHighlightsValues = hasBenchmarkValues([
    benchmarks?.igAvgStoryViews || benchmarks?.igAvgStoryImpressions,
    benchmarks?.igAvgStoryReach,
  ]);
  const hasReelsHighlightsValues = hasBenchmarkValues([
    benchmarks?.igAvgReelsPlaysInteractionsRate,
    benchmarks?.igAvgReelsViews,
    benchmarks?.igAvgReelsInteractions,
  ]);
  const hasAudiencySummaryValues =
    insight?.showAges || insight?.showCountries || insight?.showGenders;

  const maxWidth = alignmentFFEnabled ? '550px' : '624px';

  return !!hasPostsHighlightsValues || !!hasAudiencySummaryValues
    ? `
    <table
        style="
        font-family: 'Roboto', sans-serif;
        border: 1px #dee2e8 solid;
        border-radius: 12px;
        margin-bottom: 12px;
        padding: 24px;
        min-width: ${maxWidth};
        "
    >
    ${
      platforms.instagram?.higher
        ? `
    <tr>
      <td colspan="2">
      ${renderCreatorPlatformEngagementDetailLevelInfo(
        creator,
        SOCIAL_PLATFORMS.instagram,
      )}
      </td>
    </tr>
    `
        : ``
    }
    ${`<tr>
      <td colspan="2">
        <table>
          <thead align="left">
            <tr>
              ${
                hasPostsHighlightsValues
                  ? `<th>
                <span
                  style="
                    color: #1c2128;
                    font-size: 11px;
                    font-weight: bold;
                    line-height: 22px;
                    padding-right: 48px;
                  "
                  >
                  Instagram Posts Highlights</span
                >
              </th>`
                  : ''
              }
              ${
                hasAudiencySummaryValues
                  ? `<th
                style="
                  color: #303d4f;
                  font-size: 11px;
                  font-weight: bold;
                  line-height: 18px;
                "
              >
                <span>Instagram Audience Summary</span>
              </th>`
                  : ''
              }
            </tr>
          </thead>
          <tbody valign="top">
            <tr>
              ${
                hasPostsHighlightsValues
                  ? `<td>
                <table>
                  ${
                    benchmarks?.igAvgFeedReelsFollowerEngRate
                      ? `<tr>
                    <td>
                      <span
                        style="
                          color: #3a495f;
                          font-size: 11px;
                          font-weight: bold;
                          margin-right: 6px;
                        "
                        >
                        ${formatPercentageWithOneDecimal(
                          benchmarks?.igAvgFeedReelsFollowerEngRate,
                        )}
                        </span>
                      <span style="color: #3a495f; font-size: 11px"
                        >Follower Eng Rate</span
                      >
                    </td>
                  </tr>`
                      : ''
                  }
                  ${
                    benchmarks?.igAvgFeedReelsReachEngRate
                      ? `<tr>
                    <td>
                      <span
                        style="
                          color: #3a495f;
                          font-size: 11px;
                          font-weight: bold;
                          margin-right: 6px;
                        "
                        >${formatPercentageWithOneDecimal(
                          benchmarks?.igAvgFeedReelsReachEngRate,
                        )}</span>
                      <span style="color: #3a495f; font-size: 11px"
                        >Reach Eng Rate</span
                      >
                    </td>
                  </tr>`
                      : ''
                  }
                  ${
                    benchmarks?.igAvgFeedReelsEngagements
                      ? `<tr>
                    <td>
                      <span
                        style="
                          color: #3a495f;
                          font-size: 11px;
                          font-weight: bold;
                          margin-right: 6px;
                        "
                        >${formatAbbr(
                          benchmarks?.igAvgFeedReelsEngagements,
                        )}</span>
                      <span style="color: #3a495f; font-size: 11px"
                        >Avg Engagements</span
                      >
                    </td>
                  </tr>`
                      : ''
                  }
                </table>
              </td>`
                  : ''
              }              
              ${
                hasAudiencySummaryValues
                  ? `<td>
                <table>
                  <tbody valign="top">
                    <tr>
                      ${
                        insight?.showCountries
                          ? `
                        <td>
                          <table style="padding-right: 24px">
                          ${
                            insight.countries && insight.countries.length
                              ? insight.countries
                                  .map(
                                    ({ label, value }) => `
                          <tr>
                              <td>
                              <span
                                  style="
                                      color: #54657d;
                                      font-size: 11px;
                                      font-weight: bold;
                                  "
                              >
                                  ${label}
                              </span>
                              </td>
                              <td>
                              <span style="color: #8b94a2; font-size: 11px">
                                  ${formatPercentageWithOneDecimal(value)}
                              </span>
                              </td>
                          </tr>
                          `,
                                  )
                                  .join('')
                              : ''
                          }
                          </table>
                        </td>
                      `
                          : ''
                      }
                      ${
                        insight?.showGenders
                          ? `
                        <td>
                        <!-- Genders -->
                            <table style="padding-right: 24px">
                                ${insight?.genders
                                  ?.map(
                                    ({ label, value }) => `
                                <tr>
                                    <td>
                                        <span
                                        style="
                                            color: #54657d;
                                            font-size: 11px;
                                            font-weight: bold;
                                            text-transform: capitalize;
                                        "
                                        >${label}</span
                                        >
                                    </td>
                                    <td>
                                        <span style="color: #8b94a2; font-size: 11px"
                                        >${formatPercentageWithOneDecimal(
                                          value,
                                        )}</span
                                        >
                                    </td>
                                </tr>
                                `,
                                  )
                                  .join('')}
                            </table>
                        </td>
                      `
                          : ''
                      }
                      ${
                        insight?.showAges
                          ? `
                        <td>
                        <!-- Ages -->
                            <table style="padding-right: 24px">
                                ${insight?.ages
                                  ?.map(
                                    ({ label, value }) => `
                                <tr>
                                    <td>
                                        <span
                                        style="
                                            color: #54657d;
                                            font-size: 11px;
                                            font-weight: bold;
                                        "
                                        >${label}</span
                                        >
                                    </td>
                                    <td>
                                        <span style="color: #8b94a2; font-size: 11px"
                                        >${formatPercentageWithOneDecimal(
                                          value,
                                        )}</span
                                        >
                                    </td>
                                </tr>
                                `,
                                  )
                                  .join('')}
                            </table>
                        </td>
                      `
                          : ''
                      }
                    </tr>
                  </tbody>
                </table>
              </td>`
                  : ''
              }
            </tr>
          </tbody>
        </table>
      </td>
    </tr>`}

    <tr></tr>
    ${
      hasStoriesHighlightsValues
        ? `<tr>
      <td>
        <table>
          <thead align="left">
            <tr>
              <th>
                <span
                  style="
                    color: #1c2128;
                    font-size: 11px;
                    font-weight: bold;
                    line-height: 22px;
                    padding-right: 48px;
                  "
                  >Instagram Stories Highlights</span
                >
              </th>
            </tr>
          </thead>
          <tbody valign="top">
            <tr>
              <!-- Instagram Stories Highlights -->
              <td>
                <table>
                  ${
                    benchmarks?.igAvgStoryViews ||
                    benchmarks?.igAvgStoryImpressions
                      ? `<tr>
                    <td>
                      <span
                        style="
                            color: #3a495f;
                            font-size: 11px;
                            font-weight: bold;
                            margin-right: 6px;
                            "
                        >
                            ${formatAbbr(
                              benchmarks
                                ? parseFloat(
                                    String(
                                      benchmarks?.igAvgStoryViews ||
                                        benchmarks?.igAvgStoryImpressions,
                                    ),
                                  )
                                : undefined,
                            )}
                        </span>
                      <span style="color: #3a495f; font-size: 11px"
                        >Avg Story Views</span
                      >
                    </td>
                  </tr>`
                      : ''
                  }
                  ${
                    benchmarks?.igAvgStoryReach
                      ? `<tr>
                    <td>
                      <span
                        style="
                            color: #3a495f;
                            font-size: 11px;
                            font-weight: bold;
                            margin-right: 6px;
                            "
                        >
                            ${formatAbbr(
                              benchmarks
                                ? parseFloat(
                                    String(benchmarks?.igAvgStoryReach),
                                  )
                                : undefined,
                            )}
                        </span>
                      <span style="color: #3a495f; font-size: 11px"
                        >Avg Story Reach</span
                      >
                    </td>
                  </tr>`
                      : ''
                  }
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </td>

      <td>
        <tr></tr>
      </td>
    </tr>`
        : ''
    }

    ${
      hasReelsHighlightsValues
        ? `<tr>
    <td>
      <table>
        <thead align="left">
          <tr>
            <th>
              <span
                style="
                  color: #1c2128;
                  font-size: 11px;
                  font-weight: bold;
                  line-height: 22px;
                  padding-right: 48px;
                "
                >Instagram Reels Highlights</span
              >
            </th>
          </tr>
        </thead>
        <tbody valign="top">
          <tr>
            <!-- Instagram Reels Highlights -->
            <td>
              <table>
                ${
                  benchmarks?.igAvgReelsPlaysInteractionsRate
                    ? `<tr>
                  <td>
                    <span
                      style="
                          color: #3a495f;
                          font-size: 11px;
                          font-weight: bold;
                          margin-right: 6px;
                          "
                      >
                          ${formatPercentageWithOneDecimal(
                            benchmarks
                              ? parseFloat(
                                  String(
                                    benchmarks?.igAvgReelsPlaysInteractionsRate,
                                  ),
                                )
                              : undefined,
                          )}
                      </span>
                    <span style="color: #3a495f; font-size: 11px"
                      >View Eng Rate</span
                    >
                  </td>
                </tr>`
                    : ''
                }
                ${
                  benchmarks?.igAvgReelsViews
                    ? `<tr>
                  <td>
                    <span
                      style="
                          color: #3a495f;
                          font-size: 11px;
                          font-weight: bold;
                          margin-right: 6px;
                          "
                      >
                          ${formatAbbr(
                            benchmarks
                              ? parseFloat(String(benchmarks?.igAvgReelsViews))
                              : undefined,
                          )}
                      </span>
                    <span style="color: #3a495f; font-size: 11px"
                      >Avg Reels Views</span
                    >
                  </td>
                </tr>`
                    : ''
                }
                ${
                  benchmarks?.igAvgReelsInteractions
                    ? `<tr>
                  <td>
                    <span
                      style="
                          color: #3a495f;
                          font-size: 11px;
                          font-weight: bold;
                          margin-right: 6px;
                          "
                      >
                          ${formatAbbr(
                            benchmarks
                              ? parseFloat(
                                  String(benchmarks?.igAvgReelsInteractions),
                                )
                              : undefined,
                          )}
                      </span>
                    <span style="color: #3a495f; font-size: 11px"
                      >Avg Engagements</span
                    >
                  </td>
                </tr>`
                    : ''
                }
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>`
        : ''
    }
  </table>
  ${
    benchmarks?.igLastUpdatedAt
      ? `
    <p
      style="
        font-family: 'Roboto', sans-serif;
        display: inline-block;
        margin-bottom: 16px;
        padding: 0 16px;
        border-radius: 8px;
        background-color: #effbf8;
        height: 24px;
        line-height: 24px;
        color: #2ea98e;
        font-size: 11px;
        text-decoration: none;
      "
    >
      <img
        style="vertical-align: middle; width: 16px; height: 16px"
        width="16"
        height="16"
        src="https://assets.usefoam.com/extension/check-icon.png"
        alt=""
      />
      Certified directly from the Instagram API ${formatDate(
        benchmarks?.igLastUpdatedAt,
        MONTH_NAME_DAY_YEAR_FORMAT,
      )}
    </p>
    `
      : ''
  }
    `
    : '';
};

const getTiktokDetailEmail = (
  data: ReturnType<typeof getDetailedTemplateData>,
  creator: ICreator,
  alignmentFFEnabled: boolean,
) => {
  const { benchmarks, ttInsight, platforms } = data;

  const hasHighlightsValues = hasBenchmarkValues([
    benchmarks?.ttAvgViewsEngRate,
    benchmarks?.ttAvgViews,
    benchmarks?.ttAvgEngagements,
  ]);
  const hasAudiencySummaryValues =
    ttInsight?.showAges || ttInsight?.showCountries || ttInsight?.showGenders;

  const maxWidth = alignmentFFEnabled ? '550px' : '624px';

  return !!hasHighlightsValues || !!hasAudiencySummaryValues
    ? `
  <table
    style="
      font-family: 'Roboto', sans-serif;
      border: 1px #dee2e8 solid;
      border-radius: 12px;
      margin-bottom: 12px;
      padding: 24px;
      min-width: ${maxWidth};
    "
  >
    <thead align="left">
    ${
      platforms.tiktok?.higher
        ? `
        <tr>
          <th colspan="${
            hasHighlightsValues && hasAudiencySummaryValues ? '2' : '1'
          }">
          ${renderCreatorPlatformEngagementDetailLevelInfo(
            creator,
            SOCIAL_PLATFORMS.tiktok,
          )}
          </th>
        </tr>
    `
        : ''
    }
      <tr>
        ${
          hasHighlightsValues
            ? `<th>
          <span
            style="
              color: #1c2128;
              font-size: 11px;
              font-weight: bold;
              line-height: 22px;
              padding-right: 48px;
            "
            >TikTok Highlights</span
          >
        </th>`
            : ''
        }
        ${
          hasAudiencySummaryValues
            ? `<th
          style="
            color: #303d4f;
            font-size: 11px;
            font-weight: bold;
            line-height: 18px;
          "
        >
          <span>TikTok Audience Summary</span>
        </th>`
            : ''
        }
      </tr>
    </thead>
    <tbody valign="top">
      <tr>
        <!-- Tiktok Highlights -->
        ${
          hasHighlightsValues
            ? `<td>
          <table>
            ${
              benchmarks?.ttAvgViewsEngRate
                ? `<tr>
              <td>
                <span
                  style="
                    color: #3a495f;
                    font-size: 11px;
                    font-weight: bold;
                    margin-right: 6px;
                  "
                  >${formatPercentageWithOneDecimal(
                    benchmarks?.ttAvgViewsEngRate,
                  )}</span>
                <span style="color: #3a495f; font-size: 11px"
                  >View Eng Rate</span
                >
              </td>
            </tr>`
                : ''
            }
            ${
              benchmarks?.ttAvgViews
                ? `<tr>
              <td>
                <span
                  style="
                    color: #3a495f;
                    font-size: 11px;
                    font-weight: bold;
                    margin-right: 6px;
                  "
                  >${formatAbbr(benchmarks?.ttAvgViews)}</span>
                <span style="color: #3a495f; font-size: 11px">Avg Views</span>
              </td>
            </tr>`
                : ''
            }
            ${
              benchmarks?.ttAvgEngagements
                ? `<tr>
              <td>
                <span
                  style="
                    color: #3a495f;
                    font-size: 11px;
                    font-weight: bold;
                    margin-right: 6px;
                  "
                  >${formatAbbr(benchmarks?.ttAvgEngagements)}</span>
                <span style="color: #3a495f; font-size: 11px"
                  >Avg Engagements</span
                >
              </td>
            </tr>`
                : ''
            }
          </table>
        </td>`
            : ''
        }
        <!-- Tiktok Audience Summary -->
        ${
          hasAudiencySummaryValues
            ? `<td>
          <table>
            <tbody valign="top">
              <tr>
                ${
                  ttInsight?.showCountries
                    ? `
                  <td>
                    <!-- Countries -->
                    <table style="padding-right: 24px">
                      ${ttInsight.countries
                        ?.map(
                          ({ label, value }) => `
                      <tr>
                        <td>
                          <span
                            style="
                              color: #54657d;
                              font-size: 11px;
                              font-weight: bold;
                            "
                            >${label}</span
                          >
                        </td>
                        <td>
                          <span style="color: #8b94a2; font-size: 11px">
                            ${formatPercentageWithOneDecimal(value)}
                          </span>
                        </td>
                      </tr>
                      `,
                        )
                        .join('')}
                    </table>
                  </td>
              `
                    : ''
                }
                ${
                  ttInsight?.showGenders
                    ? `
                <td>
                  <!-- Genders -->
                  <table style="padding-right: 24px">
                  ${ttInsight.genders
                    ?.map(
                      ({ label, value }) => `
                  <tr>
                    <td>
                      <span
                        style="
                          color: #54657d;
                          font-size: 11px;
                          font-weight: bold;
                          text-transform: capitalize;
                        "
                        >${label}</span
                      >
                    </td>
                    <td>
                      <span style="color: #8b94a2; font-size: 11px"
                        >${formatPercentageWithOneDecimal(value)}</span>
                    </td>
                  </tr>
                  `,
                    )
                    .join('')}
                  </table>
                </td>
                `
                    : ''
                }
                ${
                  ttInsight?.showAges
                    ? `
                <td>
                  <!-- Ages -->
                  <table style="padding-right: 24px">
                  ${ttInsight.ages
                    ?.map(
                      ({ label, value }) => `
                  <tr>
                    <td>
                      <span
                        style="
                          color: #54657d;
                          font-size: 11px;
                          font-weight: bold;
                        "
                        >${label}</span
                      >
                    </td>
                    <td>
                      <span style="color: #8b94a2; font-size: 11px"
                        >${formatPercentageWithOneDecimal(value)}</span>
                    </td>
                  </tr>
                  `,
                    )
                    .join('')}
                  </table>
                </td>
                `
                    : ''
                }
              </tr>
            </tbody>
          </table>
        </td>`
            : ''
        }
      </tr>
    </tbody>
  </table>
  ${
    benchmarks?.ttLastUpdatedAt
      ? `
    <p
      style="
        font-family: 'Roboto', sans-serif;
        display: inline-block;
        margin-bottom: 16px;
        padding: 0 16px;
        border-radius: 8px;
        background-color: #effbf8;
        height: 24px;
        line-height: 24px;
        color: #2ea98e;
        font-size: 11px;
        text-decoration: none;
      "
    >
      <img
        style="vertical-align: middle; width: 16px; height: 16px"
        width="16"
        height="16"
        src="https://assets.usefoam.com/extension/check-icon.png"
        alt=""
      />
      Certified directly from the TikTok API ${formatDate(
        benchmarks?.ttLastUpdatedAt,
        MONTH_NAME_DAY_YEAR_FORMAT,
      )}
    </p>
  `
      : ''
  }
  `
    : '';
};

const getYoutubeDetailEmail = (
  data: ReturnType<typeof getDetailedTemplateData>,
  creator: ICreator,
  alignmentFFEnabled: boolean,
) => {
  const { benchmarks, ytInsight, platforms } = data;

  const hasHighlightsValues = hasBenchmarkValues([
    benchmarks?.ytAvgViewsEngRate,
    benchmarks?.ytAvgViews,
    benchmarks?.ytAvgEngagements,
  ]);
  const hasAudiencySummaryValues =
    ytInsight?.showAges || ytInsight?.showCountries || ytInsight?.showGenders;

  const maxWidth = alignmentFFEnabled ? '550px' : '624px';

  return !!hasHighlightsValues || !!hasAudiencySummaryValues
    ? `
    <table
      style="
        font-family: 'Roboto', sans-serif;
        border: 1px #dee2e8 solid;
        border-radius: 12px;
        margin-bottom: 12px;
        padding: 24px;
        min-width: ${maxWidth};
      "
    >
      <thead align="left">
      ${
        platforms.youtube?.higher
          ? `
        <tr>
          <th colspan="${
            hasHighlightsValues && hasAudiencySummaryValues ? '2' : '1'
          }">
            ${renderCreatorPlatformEngagementDetailLevelInfo(
              creator,
              SOCIAL_PLATFORMS.youtube,
            )}
          </th>
        </tr>
      `
          : ''
      }
        <tr>
          ${
            hasHighlightsValues
              ? `<th>
            <span
              style="
                color: #1c2128;
                font-size: 11px;
                font-weight: bold;
                line-height: 22px;
                padding-right: 48px;
              "
              >YouTube Highlights</span
            >
          </th>`
              : ''
          }
          ${
            hasAudiencySummaryValues
              ? `<th
            style="
              color: #303d4f;
              font-size: 11px;
              font-weight: bold;
              line-height: 18px;
            "
          >
            <span>YouTube Audience Summary</span>
          </th>`
              : ''
          }
        </tr>
      </thead>
      <tbody valign="top">
        <tr>
          <!-- YouTube Highlights -->
          ${
            hasHighlightsValues
              ? `<td>
            <table>
              ${
                benchmarks?.ytAvgViews
                  ? `<tr>
                <td>
                  <span
                    style="
                      color: #3a495f;
                      font-size: 11px;
                      font-weight: bold;
                      margin-right: 6px;
                    "
                    >
                    ${formatAbbr(benchmarks?.ytAvgViews)}
                  </span>
                  <span style="color: #3a495f; font-size: 11px"
                    >Avg Views</span
                  >
                </td>
              </tr>`
                  : ''
              }
            </table>
          </td>`
              : ''
          }
          <!-- YouTube Audience Summary -->
          ${
            hasAudiencySummaryValues
              ? `<td>
            <table>
              <tbody valign="top">
                <tr>
                ${
                  ytInsight?.showCountries
                    ? `
                  <td>
                    <!-- Countries -->
                    <table style="padding-right: 24px">
                      ${ytInsight.countries
                        ?.map(
                          ({ label, value }) => `
                        <tr>
                          <td>
                            <span
                              style="
                                color: #54657d;
                                font-size: 11px;
                                font-weight: bold;
                              "
                              >${label}</span
                            >
                          </td>
                          <td>
                            <span style="color: #8b94a2; font-size: 11px"
                              >${formatPercentageWithOneDecimal(value)}</span>
                          </td>
                        </tr>
                      `,
                        )
                        .join('')}
                    </table>
                  </td>
                `
                    : ''
                }
                  ${
                    ytInsight?.showGenders
                      ? `
                    <td>
                    <!-- Genders -->
                    <table style="padding-right: 24px">
                      ${ytInsight.genders
                        ?.map(
                          ({ label, value }) => `
                        <tr>
                          <td>
                            <span
                              style="
                                color: #54657d;
                                font-size: 11px;
                                font-weight: bold;
                              "
                              >${label}</span
                            >
                          </td>
                          <td>
                            <span style="color: #8b94a2; font-size: 11px"
                              >${formatPercentageWithOneDecimal(value)}</span>
                          </td>
                        </tr>
                      `,
                        )
                        .join('')}
                    </table>
                  </td>
                  `
                      : ''
                  }
                  ${
                    ytInsight?.showAges
                      ? `
                    <td>
                    <!-- Ages -->
                    <table style="padding-right: 24px">
                      ${ytInsight.ages
                        ?.map(
                          ({ label, value }) => `
                        <tr>
                          <td>
                            <span
                              style="
                                color: #54657d;
                                font-size: 11px;
                                font-weight: bold;
                              "
                              >${label}</span
                            >
                          </td>
                          <td>
                            <span style="color: #8b94a2; font-size: 11px"
                              >${formatPercentageWithOneDecimal(value)}</span>
                          </td>
                        </tr>
                      `,
                        )
                        .join('')}
                    </table>
                  </td>
                  `
                      : ''
                  }
                </tr>
              </tbody>
            </table>
          </td>`
              : ''
          }
        </tr>
      </tbody>
    </table>
    ${
      benchmarks?.ytLastUpdatedAt
        ? `
    <p
    style="
      font-family: 'Roboto', sans-serif;
      display: inline-block;
      margin-bottom: 16px;
      padding: 0 16px;
      border-radius: 8px;
      background-color: #effbf8;
      height: 24px;
      line-height: 24px;
      color: #2ea98e;
      font-size: 11px;
      text-decoration: none;
    "
  >
    <img
      style="vertical-align: middle; width: 16px; height: 16px"
      width="16"
      height="16"
      src="https://assets.usefoam.com/extension/check-icon.png"
      alt=""
    />
    Certified directly from the YouTube API ${formatDate(
      benchmarks?.ytLastUpdatedAt,
      MONTH_NAME_DAY_YEAR_FORMAT,
    )}
  </p>
    `
        : ''
    }
  `
    : '';
};
